// Tally Prime / Tally.ERP 9 XML builders and parsers.
//
// Everything here is a pure function (no React, no Firebase) so the same code produces the
// XML for both the "Download XML" button and the queue that the helper app on the Tally server
// posts to http://localhost:9000.
//
// The structures follow Tally's documented XML import format (Import Data -> All Masters / Vouchers).
// They have NOT been run against your Tally yet - test one ledger, one voucher first (see the README
// in tools/tally-bridge). Names of ledgers / voucher types / company come from Settings > Tally.

// ---------------------------------------------------------------- basics

// Escape text for XML and drop characters XML 1.0 does not allow.
/* eslint-disable no-control-regex */
export const esc = (v) => String(v === undefined || v === null ? '' : v)
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
/* eslint-enable no-control-regex */

// GST ledger names can carry the rate, e.g. "Output CGST@{rate}%" -> "Output CGST@9%" (rate = the rate of THAT tax: 9 for CGST/SGST at 18%).
export const fillRate = (name, rate) => String(name || '').replace(/\{rate\}/g, String(Number(rate)));
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const money = (n) => round2(n).toFixed(2);

// '2026-10-15' (or an ISO timestamp) -> '20261015'
export const tallyDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (m) return `${m[1]}${m[2]}${m[3]}`;
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

// GSTIN state codes -> Tally state names
export const GST_STATES = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
  '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh', '13': 'Nagaland',
  '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
  '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat', '26': 'Dadra & Nagar Haveli and Daman & Diu',
  '27': 'Maharashtra', '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh', '38': 'Ladakh'
};
export const stateFromGstin = (gstin) => GST_STATES[String(gstin || '').trim().slice(0, 2)] || '';
export const isGstin = (g) => /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(String(g || '').trim().toUpperCase());

/**
 * GST on a net amount: CGST+SGST (half each) when the party is in the seller's state, otherwise IGST.
 * Used by the invoice PDF and the Tally voucher so the two always agree to the paisa.
 */
export const gstTotals = (net, rate, partyState, ownState) => {
  const r = rate === undefined || rate === null || rate === '' ? 18 : Number(rate);
  const interstate = !!partyState && !!ownState && String(partyState).toLowerCase() !== String(ownState).toLowerCase();
  const n = round2(net);
  const cgst = interstate ? 0 : round2(n * r / 200);
  const sgst = interstate ? 0 : round2(n * r / 200);
  const igst = interstate ? round2(n * r / 100) : 0;
  return { net: n, rate: r, interstate, cgst, sgst, igst, total: round2(n + cgst + sgst + igst) };
};

// Settings > Tally defaults (merged with what the user saved in config/tally)
export const DEFAULT_TALLY_COMPANY_SETTINGS = {
  tallyCompany: '',                 // exact company name as shown in Tally (required)
  stateName: 'Karnataka',           // state of the Tally company (decides CGST+SGST vs IGST)
  debtorsGroup: 'Sundry Debtors',
  ledgers: {
    rental: 'Rental Income',
    sales: 'Sales',
    service: 'Service Income',
    cgst: 'Output CGST',
    sgst: 'Output SGST',
    igst: 'Output IGST'
  },
  vouchers: { sales: 'Sales', order: 'Memorandum', memo: 'Memorandum' },   // quotations, proformas and challans go as non-posting Memorandum vouchers (no stock items needed)
  supplyVoucherNumber: true,        // true: the voucher number in Tally IS the portal's document number (series stay in step); false: Tally numbers them itself
  autoSync: { clients: true, invoices: true, quotations: true, proformas: true, challans: true }   // send to Tally the moment they are saved
};

export const mergeTallySettings = (saved) => {
  const s = saved || {};
  return {
    ...DEFAULT_TALLY_COMPANY_SETTINGS, ...s,
    ledgers: { ...DEFAULT_TALLY_COMPANY_SETTINGS.ledgers, ...(s.ledgers || {}) },
    vouchers: { ...DEFAULT_TALLY_COMPANY_SETTINGS.vouchers, ...(s.vouchers || {}) },
    autoSync: { ...DEFAULT_TALLY_COMPANY_SETTINGS.autoSync, ...(s.autoSync || {}) }
  };
};

// ---------------------------------------------------------------- envelopes

const importEnvelope = (reportName, company, messagesXml) => `<ENVELOPE>
  <HEADER>
    <VERSION>1</VERSION>
    <TALLYREQUEST>Import</TALLYREQUEST>
    <TYPE>Data</TYPE>
    <ID>${esc(reportName)}</ID>
  </HEADER>
  <BODY>
    <DESC>
      <STATICVARIABLES>
        <SVCURRENTCOMPANY>${esc(company)}</SVCURRENTCOMPANY>
      </STATICVARIABLES>
    </DESC>
    <DATA>
      <TALLYMESSAGE xmlns:UDF="TallyUDF">
${messagesXml}
      </TALLYMESSAGE>
    </DATA>
  </BODY>
</ENVELOPE>`;

// ---------------------------------------------------------------- ledgers (masters)

/**
 * One ledger. `action` is 'Create' (default). Tally refuses to Create an existing ledger - the helper
 * treats that as "already exists" and marks it done.
 * ledger: { name, parent, address (string, newlines ok), state, gstin, phone, email, billwise }
 */
export const ledgerMessage = (l, action = 'Create') => {
  const addr = String(l.address || '').split(/\r?\n|,\s{2,}/).map(x => x.trim()).filter(Boolean).slice(0, 4);
  const gstin = String(l.gstin || '').trim().toUpperCase();
  const state = l.state || stateFromGstin(gstin);
  const lines = [];
  lines.push(`        <LEDGER NAME="${esc(l.oldName || l.name)}" ACTION="${esc(action)}">`);   // oldName: rename an existing ledger
  lines.push(`          <NAME.LIST><NAME>${esc(l.name)}</NAME></NAME.LIST>`);
  lines.push(`          <PARENT>${esc(l.parent)}</PARENT>`);
  if (l.billwise !== false && /debtor|creditor/i.test(l.parent || '')) lines.push('          <ISBILLWISEON>Yes</ISBILLWISEON>');
  if (addr.length) lines.push(`          <ADDRESS.LIST TYPE="String">${addr.map(a => `<ADDRESS>${esc(a)}</ADDRESS>`).join('')}</ADDRESS.LIST>`);
  if (state) { lines.push('          <COUNTRYNAME>India</COUNTRYNAME>'); lines.push(`          <LEDSTATENAME>${esc(state)}</LEDSTATENAME>`); }
  if (gstin) {
    lines.push('          <GSTREGISTRATIONTYPE>Regular</GSTREGISTRATIONTYPE>');
    lines.push(`          <PARTYGSTIN>${esc(gstin)}</PARTYGSTIN>`);
  } else if (/debtor/i.test(l.parent || '')) {
    lines.push('          <GSTREGISTRATIONTYPE>Unregistered/Consumer</GSTREGISTRATIONTYPE>');
  }
  if (l.phone) lines.push(`          <LEDGERMOBILE>${esc(l.phone)}</LEDGERMOBILE>`);
  if (l.email) lines.push(`          <EMAIL>${esc(l.email)}</EMAIL>`);
  lines.push('        </LEDGER>');
  return lines.join('\n');
};

export const mastersXml = (company, ledgers, action = 'Create') =>
  importEnvelope('All Masters', company, ledgers.map(l => ledgerMessage(l, action)).join('\n'));

/** Income + GST ledgers the vouchers post to (so they exist before the first voucher). */
export const standardMasters = (cfg) => {
  const L = cfg.ledgers;
  const gstLedger = (name, head) => ({
    name, parent: 'Duties & Taxes', billwise: false, _gst: head
  });
  return [
    { name: L.rental, parent: 'Sales Accounts', billwise: false },
    { name: L.sales, parent: 'Sales Accounts', billwise: false },
    { name: L.service, parent: 'Sales Accounts', billwise: false },
    gstLedger(fillRate(L.cgst, 9), 'Central Tax'), gstLedger(fillRate(L.sgst, 9), 'State Tax'), gstLedger(fillRate(L.igst, 18), 'Integrated Tax')
  ];
};

/** Masters XML for the income and GST ledgers (GST ledgers carry the duty head so Tally treats them as taxes). */
export const standardMastersXml = (cfg) => {
  const parts = standardMasters(cfg).map(l => {
    const base = ledgerMessage(l, 'Create');
    if (!l._gst) return base;
    // (no TAXCLASSIFICATIONNAME: Tally's built-in "Not Applicable" has a hidden prefix character, so naming it fails; it is optional)
    return base.replace('        </LEDGER>', `          <TAXTYPE>GST</TAXTYPE>\n          <GSTDUTYHEAD>${esc(l._gst)}</GSTDUTYHEAD>\n        </LEDGER>`);
  });
  return importEnvelope('All Masters', cfg.tallyCompany, parts.join('\n'));
};

// ---------------------------------------------------------------- vouchers

/**
 * Accounting-mode voucher (Sales, or Sales Order if the voucher type is configured that way).
 * v: {
 *   date 'YYYY-MM-DD', voucherType, party, partyGstin, partyState, narration, reference,
 *   lines: [{ ledger, amount, description }]   // amounts exclusive of GST
 *   gstRate (default 18)
 * }
 * Returns { xml (one TALLYMESSAGE body), totals: { net, cgst, sgst, igst, total } }
 */
export const voucherMessage = (v, cfg) => {
  const rate = v.gstRate === undefined ? 18 : Number(v.gstRate);
  const byLedger = new Map();
  (v.lines || []).forEach(l => byLedger.set(l.ledger, round2((byLedger.get(l.ledger) || 0) + Number(l.amount || 0))));
  const net = round2([...byLedger.values()].reduce((a, b) => a + b, 0));

  const partyState = v.partyState || stateFromGstin(v.partyGstin) || cfg.stateName;
  const { interstate, cgst, sgst, igst, total } = gstTotals(net, rate, partyState, cfg.stateName);

  const vtype = v.voucherType || cfg.vouchers.sales;
  const memo = !!v.memo || /memorandum|order/i.test(vtype);   // non-posting documents: no invoice flag, no bill allocation
  const ref = v.reference || '';
  const entries = [];
  entries.push(`          <LEDGERENTRIES.LIST>
            <LEDGERNAME>${esc(v.party)}</LEDGERNAME>
            <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
            <ISPARTYLEDGER>Yes</ISPARTYLEDGER>
            <AMOUNT>-${money(total)}</AMOUNT>${ref && !memo ? `
            <BILLALLOCATIONS.LIST>
              <NAME>${esc(ref)}</NAME>
              <BILLTYPE>New Ref</BILLTYPE>
              <AMOUNT>-${money(total)}</AMOUNT>
            </BILLALLOCATIONS.LIST>` : ''}
          </LEDGERENTRIES.LIST>`);
  const credit = (ledger, amount) => entries.push(`          <LEDGERENTRIES.LIST>
            <LEDGERNAME>${esc(ledger)}</LEDGERNAME>
            <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
            <AMOUNT>${money(amount)}</AMOUNT>
          </LEDGERENTRIES.LIST>`);
  byLedger.forEach((amt, ledger) => credit(ledger, amt));
  if (cgst) credit(fillRate(cfg.ledgers.cgst, rate / 2), cgst);
  if (sgst) credit(fillRate(cfg.ledgers.sgst, rate / 2), sgst);
  if (igst) credit(fillRate(cfg.ledgers.igst, rate), igst);

  const xml = `        <VOUCHER VCHTYPE="${esc(vtype)}" ACTION="Create" OBJVIEW="Accounting Voucher View">
          <DATE>${tallyDate(v.date)}</DATE>
          <VOUCHERTYPENAME>${esc(vtype)}</VOUCHERTYPENAME>${cfg.supplyVoucherNumber && ref ? `
          <VOUCHERNUMBER>${esc(ref)}</VOUCHERNUMBER>` : ''}${ref ? `
          <REFERENCE>${esc(ref)}</REFERENCE>` : ''}
          <PARTYLEDGERNAME>${esc(v.party)}</PARTYLEDGERNAME>${v.partyGstin ? `
          <PARTYGSTIN>${esc(String(v.partyGstin).toUpperCase())}</PARTYGSTIN>` : ''}
          <PLACEOFSUPPLY>${esc(partyState)}</PLACEOFSUPPLY>
${memo ? '' : `          <ISINVOICE>Yes</ISINVOICE>
`}          <NARRATION>${esc(v.narration || '')}</NARRATION>
${entries.join('\n')}
        </VOUCHER>`;
  return { xml, totals: { net, cgst, sgst, igst, total, interstate } };
};

export const vouchersXml = (company, vouchers, cfg) =>
  importEnvelope('Vouchers', company, vouchers.map(v => voucherMessage(v, cfg).xml).join('\n'));

// ---------------------------------------------------------------- responses

const tag = (xml, name) => { const m = new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`, 'i').exec(xml); return m ? m[1] : ''; };

/** Parse Tally's reply to an import. ok = no errors and something was created/altered. */
export const parseImportResponse = (xmlText) => {
  const x = String(xmlText || '');
  const num = (n) => parseInt(tag(x, n) || '0', 10) || 0;
  const created = num('CREATED'), altered = num('ALTERED'), errors = num('ERRORS'), ignored = num('IGNORED');
  const lineErrors = [...x.matchAll(/<LINEERROR>([\s\S]*?)<\/LINEERROR>/gi)].map(m => m[1].trim());
  const exceptions = num('EXCEPTIONS');
  const alreadyExists = lineErrors.some(e => /already exists/i.test(e));
  return {
    created, altered, errors, ignored, exceptions, lineErrors, alreadyExists,
    ok: errors === 0 && exceptions === 0 && (created + altered) > 0,
    raw: x.slice(0, 2000)
  };
};

// ---------------------------------------------------------------- export (pull) requests

const exportEnvelope = (id, company, collectionXml, extraVars = '') => `<ENVELOPE>
  <HEADER>
    <VERSION>1</VERSION>
    <TALLYREQUEST>Export</TALLYREQUEST>
    <TYPE>Collection</TYPE>
    <ID>${esc(id)}</ID>
  </HEADER>
  <BODY>
    <DESC>
      <STATICVARIABLES>
        <SVEXPORTFORMAT>$$SysName:XML</SVEXPORTFORMAT>
        <SVCURRENTCOMPANY>${esc(company)}</SVCURRENTCOMPANY>${extraVars}
      </STATICVARIABLES>
      <TDL>
        <TDLMESSAGE>
${collectionXml}
        </TDLMESSAGE>
      </TDL>
    </DESC>
  </BODY>
</ENVELOPE>`;

/** Sundry debtors with their closing balance (outstanding receivable). */
export const pullDebtorsXml = (company, group = 'Sundry Debtors') => exportEnvelope('PortalDebtors', company,
`          <COLLECTION NAME="PortalDebtors" ISMODIFY="No">
            <TYPE>Ledger</TYPE>
            <CHILDOF>${esc(group)}</CHILDOF>
            <BELONGSTO>Yes</BELONGSTO>
            <NATIVEMETHOD>Name</NATIVEMETHOD>
            <NATIVEMETHOD>ClosingBalance</NATIVEMETHOD>
            <NATIVEMETHOD>PartyGSTIN</NATIVEMETHOD>
          </COLLECTION>`);

/** Names + parent group of every ledger under a group (and its sub-groups) - used to read the real GST / income ledger names. */
export const pullLedgerListXml = (company, group) => exportEnvelope('PortalLedgerList', company,
`          <COLLECTION NAME="PortalLedgerList" ISMODIFY="No">
            <TYPE>Ledger</TYPE>
            <CHILDOF>${esc(group)}</CHILDOF>
            <BELONGSTO>Yes</BELONGSTO>
            <NATIVEMETHOD>Name</NATIVEMETHOD>
            <NATIVEMETHOD>Parent</NATIVEMETHOD>
          </COLLECTION>`);

/** Receipt vouchers between two dates (YYYY-MM-DD), via Tally's standard Day Book export filtered to the Receipt voucher type. */
export const pullReceiptsXml = (company, fromIso, toIso) => `<ENVELOPE>
  <HEADER>
    <VERSION>1</VERSION>
    <TALLYREQUEST>Export</TALLYREQUEST>
    <TYPE>Data</TYPE>
    <ID>Day Book</ID>
  </HEADER>
  <BODY>
    <DESC>
      <STATICVARIABLES>
        <SVEXPORTFORMAT>$$SysName:XML</SVEXPORTFORMAT>
        <SVCURRENTCOMPANY>${esc(company)}</SVCURRENTCOMPANY>
        <SVFROMDATE>${tallyDate(fromIso)}</SVFROMDATE>
        <SVTODATE>${tallyDate(toIso)}</SVTODATE>
        <VOUCHERTYPENAME>Receipt</VOUCHERTYPENAME>
      </STATICVARIABLES>
    </DESC>
  </BODY>
</ENVELOPE>`;

const blocks = (xml, name) => [...String(xml || '').matchAll(new RegExp(`<${name}\\b[^>]*>([\\s\\S]*?)</${name}>`, 'gi'))].map(m => m[1]);
const unesc = (s) => String(s || '')
  .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
  .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(parseInt(d, 10)))
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&')
  .replace(/\s+/g, ' ').trim();
const toNum = (s) => {
  const x = unesc(s); const n = parseFloat(x.replace(/[^0-9.-]/g, ''));
  if (isNaN(n)) return 0;
  if (/\bDr\b/i.test(x)) return -Math.abs(n);
  if (/\bCr\b/i.test(x)) return Math.abs(n);
  return n;
};

/**
 * Tally exports a debtor's closing balance as a negative number when the client owes money (a debit).
 * We return `outstanding` as a positive amount owed to us.
 */
export const parseDebtors = (xmlText) => [...String(xmlText || '').matchAll(/<LEDGER\b([^>]*)>([\s\S]*?)<\/LEDGER>/gi)].map(m => {
  const nm = /NAME="([^"]*)"/i.exec(m[1]);
  const closing = toNum(tag(m[2], 'CLOSINGBALANCE'));
  return { name: unesc(nm ? nm[1] : tag(m[2], 'NAME')), gstin: unesc(tag(m[2], 'PARTYGSTIN')), closing, outstanding: round2(-closing) || 0 };
}).filter(x => x.name);

export const parseReceipts = (xmlText) => blocks(xmlText, 'VOUCHER').map(b => ({
  date: unesc(tag(b, 'DATE')), number: unesc(tag(b, 'VOUCHERNUMBER')), type: unesc(tag(b, 'VOUCHERTYPENAME')),
  party: unesc(tag(b, 'PARTYLEDGERNAME')), amount: Math.abs(toNum(tag(b, 'AMOUNT'))), narration: unesc(tag(b, 'NARRATION'))
})).filter(x => /receipt/i.test(x.type) && x.party && x.amount);
